import type Sns from '@lambda/commons/apis/sns';
import {
  ReebeloB2bStoreT,
  ReebeloConditions,
  ReebeloGtinRegion,
  ReebeloLanguage,
  ReebeloSalesChannelT,
  ReebeloStoreT,
  ReebeloVendorsInvetoryOrigins,
} from '@lambda/reebelo';
import { Variant } from '@lambda/reebelo/src/tagHelpers';
import {
  VendorAgreementDoc,
  VendorAgreementRecord,
} from '@lambda/business/vendors/agreements/types';
import { ProductFaqs } from '@lambda/commons/types';
import { ServiceProvider } from '@lambda/commons/apis/elasticsearch/types';

export type DbObject<T extends Record<any, any>> = T & {
  pk: string;
  sk: string;
  'gsi-pk'?: string;
  'gsi-sk'?: string;
  'lsi-sk'?: string;
  'lsi-2-sk'?: string;
  'lsi-3-sk'?: string;
  ttl?: string;
};

export enum PKs {
  // AUTOMATIONS
  'OFFER' = 'OFFER',
  'SHOPPING_PROXY_CRAWL_SERVERS' = 'SHOPPING_PROXY_CRAWL_SERVERS',
  'REEEBLO_SHIPPING_CONFIG' = 'REEEBLO_SHIPPING_CONFIG',
  // PIM
  'PIMPRODUCT' = 'PIMPRODUCT',
  // VENDOR
  'VENDOR_DETAILS' = 'VENDOR_DETAILS',
  'VENDOR_API' = 'VENDOR_API',
  'VENDOR_INTEGRATION' = 'VENDOR_INTEGRATION',
  'VENDOR_B2B_DETAILS' = 'VENDOR_B2B_DETAILS',
  // B2B
  'B2B_ACCOUNT_REQUEST' = 'B2B_ACCOUNT_REQUEST',
  // STOREFRONT
  'ORDER_COUNT' = 'ORDER_COUNT',
  'COLLECTION_NODE' = 'COLLECTION_NODE',
  'PRODUCT_CATEGORY' = 'PRODUCT_CATEGORY',
  // Recently Viewed
  'RECENTLY_VIEWED_DETAILS' = 'RECENTLY_VIEWED_DETAILS',
  // CUSTOMER ACCEPTS MARKETING
  'CUSTOMER_ACCEPTS_MARKETING' = 'CUSTOMER_ACCEPTS_MARKETING',
  'PAYMENT_GATEWAY_REFUND' = 'PAYMENT_GATEWAY_REFUND',
  // Vendor Agreements
  'VENDOR_AGREEMENT_DOC' = 'VENDOR_AGREEMENT_DOC',
  'VENDOR_AGREEMENT_RECORD' = 'VENDOR_AGREEMENT_RECORD',
  // Partnerships
  'WALMART_INTEGRATION_PRODUCT' = 'PRODUCT#US',
}

export const MapPkToTableName: {
  [key in PKs]: 'database' | 'WalmartIntegration';
} = {
  [PKs.OFFER]: 'database',
  [PKs.SHOPPING_PROXY_CRAWL_SERVERS]: 'database',
  [PKs.PIMPRODUCT]: 'database',
  [PKs.ORDER_COUNT]: 'database',
  [PKs.VENDOR_DETAILS]: 'database',
  [PKs.VENDOR_API]: 'database',
  [PKs.VENDOR_INTEGRATION]: 'database',
  [PKs.VENDOR_B2B_DETAILS]: 'database',
  [PKs.B2B_ACCOUNT_REQUEST]: 'database',
  [PKs.COLLECTION_NODE]: 'database',
  [PKs.PRODUCT_CATEGORY]: 'database',
  [PKs.RECENTLY_VIEWED_DETAILS]: 'database',
  [PKs.REEEBLO_SHIPPING_CONFIG]: 'database',
  [PKs.CUSTOMER_ACCEPTS_MARKETING]: 'database',
  [PKs.PAYMENT_GATEWAY_REFUND]: 'database',
  [PKs.VENDOR_AGREEMENT_DOC]: 'database',
  [PKs.VENDOR_AGREEMENT_RECORD]: 'database',
  [PKs.WALMART_INTEGRATION_PRODUCT]: 'WalmartIntegration',
};

export type MapPkToKeys = {
  // AUTOMATION
  [PKs.OFFER]: OfferT;
  [PKs.SHOPPING_PROXY_CRAWL_SERVERS]: ShoppingCrawlProxyServer;
  // PIM
  [PKs.PIMPRODUCT]: PimProductT;
  [PKs.ORDER_COUNT]: OrderCountT;
  [PKs.VENDOR_DETAILS]: VendorDetails;
  [PKs.VENDOR_API]: VendorApi;
  [PKs.VENDOR_INTEGRATION]: VendorIntegration;
  [PKs.VENDOR_B2B_DETAILS]: VendorB2bDetails;
  [PKs.COLLECTION_NODE]: CollectionNodeT;
  [PKs.PRODUCT_CATEGORY]: ProductCategoryT;
  [PKs.RECENTLY_VIEWED_DETAILS]: RecentlyViewedT;
  [PKs.REEEBLO_SHIPPING_CONFIG]: ReebeloShipping;
  [PKs.B2B_ACCOUNT_REQUEST]: B2bAccountRequest;
  [PKs.CUSTOMER_ACCEPTS_MARKETING]: CustomerAcceptsMarketing;
  [PKs.PAYMENT_GATEWAY_REFUND]: PaymentGatewayRefund;
  // Vendor Agreements
  [PKs.VENDOR_AGREEMENT_DOC]: VendorAgreementDoc;
  [PKs.VENDOR_AGREEMENT_RECORD]: VendorAgreementRecord;
  // Partnerships
  [PKs.WALMART_INTEGRATION_PRODUCT]: WalmartIntegrationProductT;
};
export type CollectionNodeT = {
  path: string;
  product: PimProductT | undefined;
};
type Rec<K extends keyof any, T> = Partial<Record<K, T>>;

export enum VendorDispatchType {
  'CROSS_BORDER' = 'CROSS_BORDER',
  'LOCAL' = 'LOCAL',
}

/**
 * RT-16789: outcome of the last VIES check of a vendor's Intra-Community VAT
 * number. `SERVICE_UNAVAILABLE` is a real state, not a failure: VIES (or one
 * member state behind it) could not answer, and the number must not be
 * treated as invalid because of that.
 */
export enum IntraCommunityVatStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
}

export type CheckoutIdToOrderIdMap = {
  checkoutId: string;
  orderId: string;
};

export type CustomerAcceptsMarketing = {
  customerId: string;
  buyerAcceptsMarketing: boolean;
};

export type DeliveryDaysEstimateRange = {
  minDays: number | null;
  maxDays: number | null;
};
export type ShippingOptionEstimateRange = {
  followingDay: boolean; // true: 1-2 days, false:2-5 days
  minDays: number | null;
  maxDays: number | null;
  nextDay: boolean;
};

export type StateEstimates<
  VendorState extends string = string,
  CustState extends string = string,
> = {
  local?: Record<VendorState, Record<CustState, DeliveryDaysEstimateRange>>;
  crossBorder?: Record<
    VendorState,
    Record<CustState, DeliveryDaysEstimateRange>
  >;
};

export type AuDomesticMatrix<
  OriginState extends string,
  OriginRegion extends string,
  DestinationState extends string,
  DestinationRegion extends string,
> = Record<
  OriginState,
  Record<
    OriginRegion,
    Record<
      DestinationState,
      Record<DestinationRegion, DeliveryDaysEstimateRange>
    >
  >
>;

export enum AuExpressPostState {
  ACT = 'ACT',
  NSW = 'NSW',
  VIC = 'VIC',
  QLD = 'QLD',
  SA = 'SA',
  WA = 'WA',
  TAS = 'TAS',
  NT = 'NT',
}

export enum AuExpressPostRegion {
  Canberra = 'Canberra',
  ActOther = 'ACT Other',

  SydneyMetro = 'Sydney Metro',
  NswOuterMetro = 'NSW Outer Metro',
  NswOther = 'NSW Other',
  NswVicBorder = 'NSW VIC Border',
  NswRural = 'NSW Rural',

  MelbourneMetro = 'Melbourne Metro',
  VicOther = 'VIC Other',
  VicRural = 'VIC Rural',

  BrisbaneMetro = 'Brisbane Metro',
  QldGoldCoast = 'QLD Gold Coast',
  QldOuterMetro = 'QLD Outer Metro',
  QldOther = 'QLD Other',
  QldRural = 'QLD Rural',

  AdelaideMetro = 'Adelaide Metro',
  SaOther = 'SA Other',
  SaRural = 'SA Rural',

  PerthCbd = 'Perth CBD',
  PerthMetro = 'Perth Metro',
  WaOther = 'WA Other',
  WaRural = 'WA Rural',

  TasMetro = 'Tas Metro',
  TasDevonport = 'Tas Devonport',
  TasCountry = 'Tas Country',
  TasRemote = 'Tas Remote',

  DarwinAndKatherine = 'Darwin & Katherine',
  AliceSprings = 'Alice Springs',
  NtRural = 'NT Rural',
}

export enum AuParcelPostState {
  NSW = 'NSW',
  ACT = 'ACT',
  VIC = 'VIC',
  QLD = 'QLD',
  SA = 'SA',
  WA = 'WA',
  TAS = 'TAS',
  NT = 'NT',
}

export enum AuParcelPostRegion {
  SydneyMetro = 'Sydney Metro',
  NswCountry = 'NSW Country',

  Canberra = 'Canberra',

  MelbourneMetro = 'Melbourne Metro',
  VicCountry = 'VIC Country',

  BrisbaneMetro = 'Brisbane Metro',
  QldNearCountry = 'QLD Near Country',
  QldFarCountry = 'QLD Far Country',
  FarNorthQld = 'Far North QLD',
  FarNorthQldRemote = 'Far North QLD Remote',

  AdelaideMetro = 'Adelaide Metro',
  SaCountry = 'SA Country',

  PerthMetro = 'Perth Metro',
  WaCountry = 'WA Country',
  WaCountryFar = 'WA Country Far',

  Tasmania = 'Tasmania',
  TasmaniaRemote = 'Tasmania Remote*',

  Darwin = 'Darwin',
  AliceSprings = 'Alice Springs',
  NtOther = 'NT Other*',
}

export type AuPostDeliveryMatrix<
  State extends string,
  Region extends string,
> = AuDomesticMatrix<State, Region, State, Region>;

export type AuExpressPostMatrix = AuPostDeliveryMatrix<
  AuExpressPostState,
  AuExpressPostRegion
>;

export type AuParcelPostMatrix = AuPostDeliveryMatrix<
  AuParcelPostState,
  AuParcelPostRegion
>;

export type CategoryEstimates<Category extends string = string> = {
  local?: Record<Category, ShippingOptionEstimateRange>;
  crossBorder?: Record<Category, ShippingOptionEstimateRange>;
};

export type ReebeloShippingByLocation = {
  store: 'reebelo-nz' | 'reebelo-ca';
  perCourier: Record<
    string,
    {
      key: string;
      name: string;
      estimates: StateEstimates;
    }
  >;
  perCategory?: CategoryEstimates;
};

export type ReebeloShippingByAuLocation = {
  store: 'reebelo-au';
  perCourier: Record<
    string,
    {
      key: string;
      name: string;
      estimates: AuExpressPostMatrix | AuParcelPostMatrix;
    }
  >;
  perCategory?: CategoryEstimates;
};

export type ReebeloShippingByCategory = {
  store: 'reebelo-us' | 'quista';
  perCategory: CategoryEstimates;
};

export type ReebeloShipping =
  | ReebeloShippingByLocation
  | ReebeloShippingByCategory
  | ReebeloShippingByAuLocation;

export type WarrantyRate = {
  from: number;
  to: number;
  price: number;
};

export enum DASHBOARD_RATING {
  NO_IMPACT = '',
  GOOD = 'good',
  WARNING = 'warning',
  BAD = 'bad',
  WORST = 'worst',
}

export type SellerRatingPenalty = {
  from: number;
  to: number;
  penalty: number;
  ratingPenalty: string;
  ratingLabel: string;
};

export enum B2BRequestAccountStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}
export enum B2BRequestAccountType {
  SELLER = 'seller',
  BUYER = 'buyer',
}

export type B2bAccountRequest = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  companyWebsite: string;
  country: string;
  revenue: string;
  type: B2BRequestAccountType;
  comment: string;
  status: B2BRequestAccountStatus;
};

export type VendorB2bDetails = {
  store: ReebeloB2bStoreT;
  vendor: string;
  displayName: string;
  invoicePrefix: string;
  emails?: Array<string>;
  company: {
    name: string;
    registrationNumber: string;
    /* in KR only */
    representative?: string;
    /* in KR only, registration number to sell online */
    salesNumber?: string;
    country: string;
    address2?: string;
    address1: string;
  };
};
export enum CommissionPayoutT {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  DAILY = 'daily',
}

export enum FinanceTransactionConditionsT {
  NEW = 'new',
  PREMIUM = 'premium',
  REFURBISHED = 'refurbished',
}
export type FinanceTrasactionCommissionCategoryRate = Record<
  FinanceTransactionConditionsT,
  number
>;

export const FinanceTransactionConditionMap: Record<
  ReebeloConditions,
  FinanceTransactionConditionsT
> = {
  [ReebeloConditions.Acceptable]: FinanceTransactionConditionsT.REFURBISHED,
  [ReebeloConditions.Excellent]: FinanceTransactionConditionsT.REFURBISHED,
  [ReebeloConditions.Good]: FinanceTransactionConditionsT.REFURBISHED,
  [ReebeloConditions.Pristine]: FinanceTransactionConditionsT.REFURBISHED,
  [ReebeloConditions.Premium]: FinanceTransactionConditionsT.PREMIUM,
  [ReebeloConditions.LikeNew]: FinanceTransactionConditionsT.REFURBISHED,
  [ReebeloConditions.BrandNew]: FinanceTransactionConditionsT.NEW,
};

export type VendorDetailsSalesChannelCommissionT<
  Category extends string = string,
> = {
  new: number;
  premium: number;
  refurbished: number;
  expressShipping: number;
  ratesByCategory?: Record<Category, FinanceTrasactionCommissionCategoryRate>;
  ratesByCategoryPk?: Record<string, FinanceTrasactionCommissionCategoryRate>;

  reebeloOptedIn?: boolean;
  reebeloOptedInLastUpdateAt?: string;
  reebeloOptedInLastUpdateBy?: string;

  vendorOptedIn?: boolean;
  vendorOptedInLastUpdateAt?: string;
  vendorOptedInLastUpdateBy?: string;

  qualityIssues: number;
  oosCancellations: number;
};

export type VendorDetailsCommissionT = {
  reebeloCareNew: number;
  reebeloCareRef: number;
  logisticsFee: number;
  gstWithheldEndDate?: string;
  rollingReverseFee: number;
  cancellationFee: number;
  subscriptionFee: number;
  subscriptionFeeStartDate: string;
  payoutType: CommissionPayoutT;
  payoutRate?: number; // only for daily payout commission
  payoutTypeUpdatedAt?: string;
  gstHstNumber?: string;
  qstNumber?: string;
};

export enum VendorLabelFeeCharge {
  API = 'api',
  FIXED = 'fixed',
  PERCENTAGE = 'percentage',
}
export type VendorDetailsSalesChannelReturnsT = {
  directReturnToSeller: boolean;
};

export type VendorPriceModifierT = {
  type: 'percent' | 'flat';
  /** Signed, so it adjusts either way. Percent to 2dp; flat in cents. */
  value: number;
};

/**
 * The slice of `dispatch` a market may set for itself. Everything else -
 * handling times, cut-offs, weekend dispatch, inventory origin - describes
 * the vendor's warehouse and so applies to every market they sell in.
 *
 * `provider` is the exception to inheriting: a carrier from another country
 * is never a sensible default, and one that isn't in the target market's
 * config silently drops that market back to store-default estimates. Absent
 * here means unset, not "use the home market's".
 */
export type VendorMarketDispatchT = {
  provider?: string;
  expressEnabled?: boolean;
  weekendDelivery?: {
    saturday?: boolean;
    sunday?: boolean;
  };
  /** Merged per category over the home record's, not replacing it wholesale. */
  deliveryEstimatesByCategory?: Record<string, ShippingOptionEstimateRange>;
  /** Falls back to the home record's when the market has none of its own. */
  deliveryEstimatesDefault?: ShippingOptionEstimateRange;
};

export type VendorDetailsMarketT = {
  vendorOptedIn: boolean;
  vendorOptedInLastUpdateAt?: string;
  vendorOptedInLastUpdateBy?: string;

  reebeloOptedIn: boolean;
  reebeloOptedInLastUpdateAt?: string;
  reebeloOptedInLastUpdateBy?: string;

  /**
   * Set true when a seller asks to leave a market they are live in. Leaving is
   * not the seller's to make: while this is set the market stays Live and
   * provisioned (both opt-in flags stay true, so `getProvisionedMarkets` keeps
   * cross-listing and MsOffer2 keeps the offers up) and an admin resolves the
   * request — approving flips `vendorOptedIn`/`reebeloOptedIn` off, rejecting
   * just clears this. Absent means no request is outstanding.
   *
   * Enabling a market is immediate and never sets this; it only ever gates the
   * opt-out direction.
   */
  optOutRequested?: boolean;
  optOutRequestedLastUpdateAt?: string;
  optOutRequestedLastUpdateBy?: string;

  /**
   * Applied to both `targetPrice` and `minPrice` of every offer replicated
   * into this market. Absent means none — zero is never stored.
   */
  priceModifier?: VendorPriceModifierT;
  priceModifierLastUpdateAt?: string;
  priceModifierLastUpdateBy?: string;

  /**
   * What this market does differently from the home record. Sparse: a field
   * absent here follows the home market, except `provider`. Kept when the
   * seller opts out so re-opting in restores what they had.
   */
  dispatch?: VendorMarketDispatchT;
};

export type VendorDetails<Category extends string = string> = {
  key: string;
  store: ReebeloStoreT;
  vendor: string;
  /**
   * Set only on satellite records — the store whose record this one is
   * projected from. Absent means this is the writable source of truth.
   *
   * Never edit a record that has this set: it is rebuilt from its home record
   * on every write, so any direct change is silently overwritten.
   */
  homeStore?: ReebeloStoreT;
  /**
   * Markets this vendor sells in besides their home store. Lives on the home
   * record only and is stripped from satellites, which carry `homeStore`.
   */
  markets?: Partial<Record<ReebeloStoreT, VendorDetailsMarketT>>;
  displayName: string;
  invoicePrefix: string;
  emails?: Array<string>;
  /**
   * Recipients of the monthly tax invoice and credit notes (RT-16053).
   * Separate from `emails` above so a billing contact - often an
   * accounts-payable mailbox rather than a person who logs in - never starts
   * receiving order/digest mail.
   *
   * Absent (rather than []) on every record written before RT-16053, which
   * reads the same as "no billing contact set": billing documents then go to
   * the first address in `emails`, the main contact (RT-17455).
   */
  billingEmails?: Array<string>;
  isInactive?: boolean;
  company: {
    name: string;
    registrationNumber: string;
    /* in KR only */
    representative?: string;
    /* in KR only, registration number to sell online */
    salesNumber?: string;
    country: string;
    address2?: string;
    address1: string;
    reebeloCareB2B: boolean;
    usingSafeware?: boolean;
    enableGMS?: boolean;
    disableGST?: boolean;
    /**
     * RT-16789: Intra-Community VAT number, stored normalized (uppercase, no
     * separators, e.g. "FR05106823123"). The three fields below describe its
     * last VIES check and are server-managed: `setVendorDetails` recomputes
     * them from the stored record or a fresh VIES call and ignores whatever
     * the client sends. Flat rather than nested to match this record's
     * `<field>LastUpdateAt` style. Copied to satellites with the rest of
     * `company`, since they describe the same legal entity.
     */
    intraCommunityVatNumber?: string;
    intraCommunityVatStatus?: IntraCommunityVatStatus;
    /** ISO-8601 time of the VIES attempt that produced the stored status. */
    intraCommunityVatValidatedAt?: string;
    /** VIES consultation number (`requestIdentifier`): legal proof of the check. */
    intraCommunityVatConsultationNumber?: string;
  };
  /**
   * On a home record this is authored directly and doubles as the home
   * market's own settings. On a satellite it is derived - `resolveDispatch`
   * folds the home record together with that market's overrides - so writing
   * to it there is pointless, the next projection overwrites it.
   */
  dispatch: {
    /**
     * Standard handling time in business days. 0 = same day, 1 = next day, etc.
     * Replaces minDays/maxDays. Old fields are still read as deprecated fallback.
     */
    handlingTime?: number;
    /** Standard cut-off time in 24h format (e.g. "14:30"). Only relevant when handlingTime === 0. */
    cutOffTime?: string;
    /** Cut-off time used on dispatched weekend days. */
    weekendCutOffTime?: string;
    expressEnabled?: boolean;
    /** Express handling time in business days. Currently always 0 (same day) in practice. */
    expressHandlingTime?: number;
    expressCutOffTime?: string;
    /** @deprecated use handlingTime */
    minDays?: number;
    /** @deprecated use handlingTime */
    maxDays?: number;
    /** @deprecated use expressHandlingTime */
    expressMinDays?: number;
    /** @deprecated use expressHandlingTime */
    expressMaxDays?: number;
    type: VendorDispatchType;
    inventoryOrigin?: ReebeloVendorsInvetoryOrigins;
    postalCode?: string; // Only for vendors selling in AU Store
    state?: string; // Only for vendors selling in AU Store
    provider?: string; // Only for vendors selling in AU Store
    deliveryEstimatesByCategory?: Record<Category, ShippingOptionEstimateRange>; // Only for vendors selling in the US Store
    deliveryEstimatesByCategoryPk?: Record<string, ShippingOptionEstimateRange>; // For the new category tree, we use categoryPks
    /**
     * Used by any category with no entry of its own in
     * `deliveryEstimatesByCategory` - the "All categories" row (RT-17576).
     */
    deliveryEstimatesDefault?: ShippingOptionEstimateRange;
    weekendDispatch?: {
      saturday?: boolean;
      sunday?: boolean;
    };
    weekendDelivery?: {
      saturday?: boolean;
      sunday?: boolean;
    };
  };
  commissionBySalesChannel: Record<
    ReebeloSalesChannelT,
    VendorDetailsSalesChannelCommissionT
  >;
  commission: VendorDetailsCommissionT;
  safewareConfig?: {
    coverageType?: string;
    ratesByCategory?: Record<
      Category,
      Record<string, Array<WarrantyRate> | null>
    >;
  };
  safewareCoverage?: Record<
    '9_months' | '11_months',
    Record<Category, Record<'rates', Array<WarrantyRate> | null>> | null
  >;
  buybox?: {
    scoreModifier: number;
    baseVendorScore: number;
    vendorScore: number;
    sellerRating?: {
      nps?: {
        minimumVolume: number;
        ranges: Array<SellerRatingPenalty>;
      };
      oos?: {
        minimumVolume: number;
        ranges: Array<SellerRatingPenalty>;
      };
      dispatch?: {
        minimumVolume: number;
        ranges: Array<SellerRatingPenalty>;
      };
      delivery?: {
        minimumVolume: number;
        ranges: Array<SellerRatingPenalty>;
      };
      quality?: {
        minimumVolume: number;
        ranges: Array<SellerRatingPenalty>;
      };
    };
  };
  returns?: {
    refundLimit?: number;
    refundCurrentLimit?: number;
    refundLimitBreached?: boolean;
    secondaryRefundLimitBreached?: boolean;
    zendeskVendorFieldTag: string;
    tag: string; // DEPRECATED - this is ZD's identifier
    labelDisplayName?: string;
    labelFeeCharge?: {
      type: VendorLabelFeeCharge;
      value: number;
    };
    emails: Array<string>;
    primaryAddress: {
      address1: string;
      address2?: string;
      postcode: string;
      state: string;
      country: string;
      city: string; // or suburb
      phoneNumber?: string;
    };
    secondaryCategories: Array<string>; // secondary categories will use secondary address for returns
    secondaryAddress?: {
      address1: string;
      address2?: string;
      postcode: string;
      state: string;
      country: string;
      city: string; // or suburb
      phoneNumber?: string;
    };
    returnsBySalesChannel?: Record<
      ReebeloSalesChannelT,
      VendorDetailsSalesChannelReturnsT
    >;
  };
  // Exposed to vendors
  payouts?: {
    stripeAccountId?: string;
  };
  preferences?: {
    upPricerOptOut: boolean;
  };
};
export type VendorApi = {
  apiKey: string;
  /**
   * HMAC-SHA256 of the plaintext apiKey, used as the DynamoDB sort key so the
   * real key is never stored as (or recoverable from) a scannable attribute.
   * Optional only to type legacy rows written before this field existed.
   */
  apiKeyHash?: string;
  vendor: string;
  feed?: {
    pausePrice?: boolean;
    pauseStock?: boolean;
  };
};
export type VendorIntegration = {
  vendor: string;
  shipStation?: {
    credentials: {
      username?: string;
      password?: string;
      appKey?: string;
      appSecret?: string;
    };
  };
  asurion?: {
    parameterName: string;
  };
  shippingEasy?: {
    apiKey: string;
    apiSecret: string;
    storeApiKey: string;
  };
  channelEngine?: {
    url: string;
    apiKey: string;
    baseProductFeedUrl: string;
  };
  shipWorks?: {
    apiKey: string;
    webhook: {
      endpoint: string;
      apiKey: string;
      hmacSecret: string;
    };
  };
  shipRush?: {
    postInUrl: string;
  };
  wholecell?: Record<string, never>;
  webhook?: {
    endpoint: string;
    headers?: Record<string, string>;
  };
  finale?: {
    account: string;
    username: string;
    password: string;
    feed?: {
      minimumStock?: number;
      pauseStock?: boolean;
    };
  };
  sellerCloud?: {
    username: string;
    feed?: {
      pausePrice?: boolean;
      pauseStock?: boolean;
    };
    password: string;
  };
  linnworks?: {
    appKey: string;
  };
  channelAdvisor?: {
    appKey: string;
    sellerId: string;
    feed?: {
      pauseStock?: boolean;
      pausePrice?: boolean;
      pauseStatus?: boolean;
    };
  };
  shopify?: {
    account: string;
    password: string;
    feed?: {
      pause?: boolean;
      pauseStock?: boolean;
      pausePrice?: boolean;
      concurrency?: number;
      filterByTag?: string;
      discount?: number;
      location?: string;
    };
    order?: {
      pause?: boolean;
      writeOrders?: boolean;
    };
  };
  quipt?: {
    account: string;
    orderPrefix: string;
  };
  skuVault?: {
    tenantToken: string;
    userToken: string;
    feed?: {
      minimumStock?: number;
      kitSkus?: boolean;
    };
  };
  wooCommerce?: {
    store: string;
    appKey: string;
    appSecret: string;
    feed?: {
      pause?: boolean;
      pauseStock?: boolean;
      pausePrice?: boolean;
      customPriceField?: string;
      customMinPriceField?: string;
      discount?: number;
    };
    order?: {
      trackingCompanyField?: string;
      trackingNumberField?: string;
    };
  };
  neto?: {
    store: string;
    username: string;
    apiKey: string;
    feed?: {
      pause?: boolean;
      pauseStock?: boolean;
      pausePrice?: boolean;
      discount?: number;
      concurrency?: number;
    };
    order?: {
      userGroup?: string;
      shippingMethod?: string;
      shippingCost?: string;
      paymentMethod?: string;
      paymentMethodId?: string;
    };
  };
  magento?: {
    url: string;
    consumerKey: string;
    consumerSecret: string;
    accessToken: string;
    tokenSecret: string;
    feed?: {
      discount?: number;
    };
  };
  bigCommerce?: {
    storeHash: string;
    accessToken: string;
    feed?: {
      pausePrice?: boolean;
      discount?: number;
    };
  };
  sellbrite?: {
    accountToken: string;
    secretKey: string;
    feed?: {
      pausePrice?: boolean;
      discount?: number;
    };
  };
  sellerActive?: {
    accountId: string;
    password: string;
  };
  solidCommerce?: {
    accountToken: string;
    salesChannelCode: string;
    productSalesChannelCode?: string;
  };
  veeqo?: {
    apiKey: string;
    channelId: number;
    deliveryMethodId: number;
    warehouseIds?: string[];
    priorityWarehouseId?: string;
    feed?: {
      pausePrice?: boolean;
    };
  };
  odoo?: {
    baseUrl: string;
    db: string;
    username: string;
    password: string;
    port?: number;
  };
  listingMirror?: {
    username: string;
    token: string;
  };
  csvFeed?: {
    url: string;
    headers?: Record<string, string>;
  };
};
export type OrderCountT = {
  store: ReebeloStoreT;
  vendor: string;
  count: number;
};
export const SimSlotSkus: Record<SimSlot, string> = {
  DS: 'Dual Sim',
  SS: 'Single Sim',
  ES: 'Dual eSim',
  SSES: 'Single Sim + eSim',
  TPSES: '2 Physical Sim + eSim',
};
export const ConnectivitieSkus: Record<Connectivities, string> = {
  C: 'Cellular',
  W: 'WiFi',
  CW: 'Cellular + WiFi',
  '4G': '4G',
  '5G': '5G',
  G: 'GPS',
  CG: 'Cellular + GPS',
  B: 'Bluetooth',
  BL: 'Bluetooth + LTE',
};
export enum Connectivities {
  'WIFI' = 'W',
  'CELL' = 'C',
  'CELL_WIFI' = 'CW',
  '4G' = '4G',
  '5G' = '5G',
  'GPS' = 'G',
  'CELL_GPS' = 'CG',
  'BLUETOOTH' = 'B',
  'BLUETOOH_LTE' = 'BL',
}
export enum SimSlot {
  'DUAL_SIM' = 'DS',
  'SINGLE_SIM' = 'SS',
  'E_SIM' = 'ES',
  'SINGLE_SIM_E_SIM' = 'SSES',
  'TWO_PHYSICAL_SIM_E_SIM' = 'TPSES',
}
export const ConnectivitiesSkusReverse: Record<string, Connectivities> = {
  Cellular: Connectivities.CELL,
  WiFi: Connectivities.WIFI,
  'Cellular + WiFi': Connectivities.CELL_WIFI,
  '4G': Connectivities['4G'],
  '5G': Connectivities['5G'],
  GPS: Connectivities.GPS,
  'Cellular + GPS': Connectivities.CELL_GPS,
  Bluetooth: Connectivities.BLUETOOTH,
  'Bluetooth + LTE': Connectivities.BLUETOOH_LTE,
};
export const SimSlotSkusReverse: Record<string, SimSlot> = {
  'Dual Sim': SimSlot.DUAL_SIM,
  'Single Sim': SimSlot.SINGLE_SIM,
  'Dual eSim': SimSlot.E_SIM,
  'Single Sim + eSim': SimSlot.SINGLE_SIM_E_SIM,
  'Two Physical Sim + eSim': SimSlot.TWO_PHYSICAL_SIM_E_SIM,
};
export enum CellularStatus {
  'LOCKED' = 'Locked',
  'UNLOCKED' = 'Unlocked',
  'PREPAID' = 'Prepaid',
}
export enum CellularNetwork {
  'GSM' = 'GSM',
  'CDMA' = 'CDMA',
}
export enum NetworkProvider {
  'AT&T' = 'AT&T',
  'VERIZON' = 'Verizon',
  'SPRINT' = 'Sprint',
  'T-MOBILE' = 'T-Mobile',
}

export enum BuyBackAvailability {
  NotAvailable = 'NotAvailable',
  Available = 'Available',
}

export enum BatteryHealth {
  New = 'New',
  Used = 'Used',
}

export type GtinStructure = {
  color?: string;
  storage?: string;
  gtinLists: { active: Array<string>; blacklist: Array<string> };
  gtin: string | null;
  cellularStatus?: string;
  cellularNetwork?: string;
  networkProvider?: string;
  region: ReebeloGtinRegion;
  bestGtin?: string;
  backupGtins?: string[];
  blacklistGtins?: string[];
};
type LocaleAttributes =
  | 'name'
  | 'description'
  | 'shortDescription'
  | 'faq'
  | 'specifications'
  | 'keyFeatures'
  | 'brand'
  | 'category'
  | 'colors'
  | 'watchBandColors'
  | 'watchCaseColors';

export type KeyFeatureT = {
  rank: number;
  icon: string;
  title: string;
  content: string;
};

export enum ProductStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
}

export type PimProductT<
  Colors extends string = string,
  Rams extends string = string,
  Cpus extends string = string,
  Gpus extends string = string,
  WatchFaceSizes extends string = string,
  WatchCaseColors extends string = string,
  WatchCaseMaterials extends string = string,
  WatchBandTypes extends string = string,
  WatchBandColors extends string = string,
  watchBandSizes extends string = string,
  WatchBandMaterials extends string = string,
  ScreenSizes extends string = string,
  CellularStatuses extends string = string,
  CellularNetworks extends string = string,
  NetworkProviders extends string = string,
  BatteryHealthConditions extends string = string,
  Charger extends string = string,
  Version extends string = string,
  Language extends string = string,
  Touchbar extends string = string,
> = {
  /** Smartphones */
  category: string;
  /** New Category Pks */
  categoryPks: string[];
  /** Smartphones */
  subCategories: string[];
  /** iPhone 11 Pro Max */
  name: string;
  /** ['128GB', '256GB'] */
  storages: string[];
  /** [ 1.1GHz Intel Core i3,.... ]* */
  cpus: Cpus[];
  gpus: Gpus[];
  watchFaceSizes: WatchFaceSizes[];
  watchCaseColors: Record<WatchCaseColors, string>;
  watchCaseColorsInHex: Record<WatchCaseColors, string>;
  watchCaseMaterials: WatchCaseMaterials[];
  watchBandTypes: WatchBandTypes[];
  watchBandColors: Record<WatchBandColors, string>;
  watchBandColorsInHex: Record<WatchBandColors, string>;
  watchBandSizes: watchBandSizes[];
  watchBandMaterials: WatchBandMaterials[];
  screenSizes: ScreenSizes[];
  cellularStatuses: CellularStatuses[];
  cellularNetworks: CellularNetworks[];
  networkProviders: NetworkProviders[];
  batteryHealth: BatteryHealthConditions[];
  /** ['blue', 'yellow'] */
  colors: Record<Colors, string>;
  colorsInHex: Record<Colors, string>;
  colorsMaterial: Record<Colors, string>;

  /** { blue: ['https://...']} */
  images: Rec<Colors, string[]>;
  connectivities: Connectivities[];
  simSlot: SimSlot[];
  rams: Rams[];
  /** Apple */
  brand: string;
  regionalDescription?: Record<ReebeloStoreT, string>;
  regionalFaq?: Record<ReebeloStoreT, string>;
  description: string;
  shortDescription: string;
  faq: string;
  faqs?: ProductFaqs;
  specifications: {
    key: string;
    value: string;
  }[];
  keyFeatures: KeyFeatureT[];
  /** P-IPHONE11 */
  psku: string;
  suggestedProducts: Array<{ psku: string }>;
  gtins: Array<GtinStructure>;
  comparePrice: Array<{ store: ReebeloStoreT; storage: string; price: number }>;
  collectionImageUrl?: string;
  adCollectionImageUrl?: string;
  locale: Rec<ReebeloLanguage, Pick<PimProductT, LocaleAttributes>>;
  collections: string[];
  releaseDate?: string;
  device_psku?: string;
  chargers: Charger[];
  versions: Version[];
  languages: Language[];
  touchbars: Touchbar[];
  status?: ProductStatus;
  variants?: Array<{
    name: string; // name of the attribute
    fieldName: string; // field name of the attribute values in the PIM
  }>;
  rVariants?: Array<{
    variant: string;
    handle: string;
    values: string[];
  }>;
  rVariantsOrder?: string[];
  rVariantsValuesOrder?: Record<string, string[]>;
};

export type OfferT = {
  sku: string;
  rsku?: string;
  title: string;
  category: string;
  score: number;
  productId: string;
  variantId: string;
  vendorPrice: number;
  vendorMinPrice?: number;
  vendorTargetPrice?: number;
  totalPrice: number;
  handle: string;
  store: ReebeloStoreT;
  stock: number;
  vendor: string;
  variant: Variant;
  productType: string;
  brand: string;
  isBest: boolean;
  isLocal?: boolean;
  isBlacklisted?: boolean;
  isActive?: boolean;
  imageUrl: string;
  gtin: string | null;
  description: string;
  shortDescription: string;
  faq: string;
  faqs?: ProductFaqs;
  rVariants?: Array<{
    variant: string;
    value: string;
  }>;
  rskuV2?: string;
  shopifyTags?: string[];
  psku?: string;
  serviceProviders?: Array<ServiceProvider>;
  lastUpdatedAt?: number;
  shopifyStatus?: string;
  touched?: string;
};

export type ShoppingCrawlProxyServer = {
  region: ReebeloGtinRegion;
  hostname: string;
  portNumber: string;
  updatedAt: string;
};

export type ProductCategoryT = {
  store: ReebeloStoreT;
  category: string;
  isEligibleForWarranty: boolean;
  selectedProducts?: Array<{ category: string; brand?: string | null }>;
};

export type RecentlyViewedProductT = {
  name: string;
  image: string;
  type: 'product' | 'collection' | 'product-v2';
  handle: string;
  sku: string;
  tags: string[];
  addedAt: string;
  price: number;
  comparePrice: number | null;
  summary: {
    count: number;
    stars: number;
    countAbove3andAHalf: number;
    starPercentages: Record<string, number>;
  } | null;
  isCheapest?: boolean;
  vendor: string;
  featured: boolean;
};

export type RecentlyViewedT = {
  email: string;
  store: ReebeloStoreT;
  data: RecentlyViewedProductT[];
};

export enum PaymentGatewayRefundOptions {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
}
export enum PaymentGatewayRefundStatus {
  PENDING = 'pending',
  PROCESSED = 'processed',
  FAILURE = 'failure',
}

export type PaymentGatewayRefundRequestT = {
  reason: string;
  amount: number;
  requestId: string;
  paymentGatewayTransactionId: string;
  paymentGateway: PaymentGatewayRefundOptions;
  store: ReebeloStoreT;
};
export type PaymentGatewayRefund = {
  requestId: string;
  status: PaymentGatewayRefundStatus;
  paymentGateway: PaymentGatewayRefundOptions;
  expiration?: string;
  retryExpiration?: string;
  retryCount?: number;
  ttl?: number;
  payload: PaymentGatewayRefundRequestT;
};

export type DynamoEventMessage<T extends Record<any, any>> = {
  keys: { pk: string; sk: string };
} & (
  | { oldObject: DbObject<T>; newObject: DbObject<T> }
  | { oldObject: undefined; newObject: DbObject<T> }
  | { oldObject: DbObject<T>; newObject: undefined }
);
export type TopicDynamoStream<
  T extends
    | PimProductT
    | OfferT
    | VendorDetails
    | CollectionNodeT
    | PaymentGatewayRefund
    | B2bAccountRequest =
    | OfferT
    | PimProductT
    | CollectionNodeT
    | PaymentGatewayRefund
    | B2bAccountRequest,
> = Sns<DynamoEventMessage<T>, { pk: string; sk: string }>;

export type WalmartIntegrationProductT = {
  id: string;
  sku: string;
  category?: string;
  upc?: string;
  gtin?: string;
  itemId?: string;
  itemUrl?: string;
  title?: string;
  condition?: string;
  price?: number;
  minPrice?: number;
  maxPrice?: number;
  stock?: number;
  currency?: string;
  published?: boolean;
  reebeloProduct?: string;
  reebeloConditions?: string;
  rsku?: string;
  productId?: number;
  repricer?: boolean;
  isPendingReview?: boolean;
};
